import {
  offlineDb,
  cascadeDeleteIfFullySynced,
  countStuckEntities,
  shouldOverwriteFromServer,
  MAX_PUSH_RETRIES,
  type SyncStatus,
  type CustomSection,
  type OfflineRapport,
} from "./db";
import type { EntityTable } from "dexie";
import { authFetch } from "@/core/utils/auth-fetch";
import type {
  OfflineSite,
  OfflineZone,
  OfflineCampagne,
  OfflinePin,
  OfflineMesure,
} from "./db";

// ─── Verrou sync ────────────────────────────────────────────
// Empêche les suppressions de s'exécuter pendant un sync (et vice-versa).
// Sans ça, le pull peut recréer une entité qu'on vient de supprimer.

let _syncLock: Promise<void> = Promise.resolve();
let _releaseLock: (() => void) | null = null;

function acquireSyncLock(): Promise<() => void> {
  return new Promise<() => void>((resolveAcquire) => {
    const prev = _syncLock;
    let release: () => void;
    _syncLock = new Promise<void>((resolveRelease) => {
      release = resolveRelease;
    });
    prev.then(() => {
      _releaseLock = release!;
      resolveAcquire(release!);
    });
  });
}

/**
 * Attend que le sync en cours se termine avant d'exécuter `fn`.
 * Empêche aussi le sync de démarrer pendant l'exécution de `fn`.
 * À utiliser pour les suppressions côté app.
 */
export async function withSyncLock<T>(fn: () => Promise<T>): Promise<T> {
  const release = await acquireSyncLock();
  try {
    return await fn();
  } finally {
    release();
  }
}

// ─── Types ──────────────────────────────────────────────────

export type SyncState = "idle" | "syncing" | "error" | "offline";

export interface SyncResult {
  success: boolean;
  pushed: {
    sites: number;
    zones: number;
    campagnes: number;
    pins: number;
    mesures: number;
    photos: number;
  };
  pulled: {
    sites: number;
    zones: number;
    campagnes: number;
    pins: number;
    mesures: number;
    photos: number;
  };
  errors: string[];
  serverTime?: string;
  /** Entités dont la cascade-delete a été REFUSÉE car elles contenaient des
   *  données non synchronisées (C4) : à signaler à l'utilisateur. */
  preservedUnsynced?: number;
  /** Entités en échec qui ont dépassé le cap de retries (C5) : bloquées,
   *  ne sont plus re-poussées automatiquement → alerte UI. */
  stuckCount?: number;
}

// ─── Helpers ────────────────────────────────────────────────

/** Récupère le token de session NextAuth depuis les cookies */
async function getAuthHeaders(): Promise<HeadersInit> {
  const token = localStorage.getItem("ultra-mesure-token");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/** Envoie un batch d'entités au serveur via POST /api/sync/push */
async function pushEntities(
  entityType: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  entities: Record<string, any>[]
): Promise<{ success: boolean; failedIds?: string[]; error?: string }> {
  try {
    const headers = await getAuthHeaders();
    const response = await authFetch("/api/sync/push", {
      method: "POST",
      headers,
      body: JSON.stringify({ entityType, entities }),
    });

    if (!response.ok) {
      const text = await response.text();
      return { success: false, error: `${response.status}: ${text}` };
    }

    const data = await response.json();
    return {
      success: data.success,
      failedIds: data.failedIds,
      error: data.errors?.join("; "),
    };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : "Erreur réseau",
    };
  }
}

/** Upload une photo vers le serveur */
async function pushPhoto(
  photoId: string,
  blob: Blob,
  fileName: string,
  mesureId: string
): Promise<{ success: boolean; serverUrl?: string; error?: string }> {
  try {
    const formData = new FormData();
    formData.append("file", blob, fileName);
    formData.append("photoId", photoId);
    formData.append("mesureId", mesureId);

    const response = await authFetch("/api/sync/photos", {
      method: "POST",
      body: formData,
      // Pas de Content-Type header : le navigateur le définit avec le boundary
    });

    if (!response.ok) {
      const text = await response.text();
      return { success: false, error: `${response.status}: ${text}` };
    }

    const data = await response.json();
    return { success: true, serverUrl: data.url };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : "Erreur réseau",
    };
  }
}

/** Upload le plan d'une zone vers le serveur */
async function pushPlanBlob(
  zoneId: string,
  planBlob: ArrayBuffer
): Promise<{ success: boolean; planUrl?: string; error?: string }> {
  try {
    const blob = new Blob([planBlob], { type: "image/jpeg" });
    const formData = new FormData();
    formData.append("file", blob, `${zoneId}.jpg`);
    formData.append("zoneId", zoneId);

    const response = await authFetch("/api/sync/plans", {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      const text = await response.text();
      return { success: false, error: `${response.status}: ${text}` };
    }

    const data = await response.json();
    return { success: true, planUrl: data.url };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : "Erreur réseau",
    };
  }
}

/**
 * Upload les plans des zones qui ont un planBlob en attente de sync.
 */
async function syncPlanBlobs(): Promise<{ count: number; errors: string[] }> {
  // Cas couverts :
  // 1. Plan jamais uploadé (planSyncStatus = "pending" ou undefined)
  // 2. Plan uploadé mais planUrl perdu/jamais poussé (planSyncStatus = "synced" mais planUrl null)
  const zones = await offlineDb.zones
    .filter((z) => !!z.planBlob && (z.planSyncStatus !== "synced" || !z.planUrl))
    .toArray();

  if (zones.length === 0) return { count: 0, errors: [] };

  let count = 0;
  const errors: string[] = [];

  for (const zone of zones) {
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      errors.push("Connexion perdue pendant l'upload des plans");
      break;
    }

    const result = await pushPlanBlob(zone.id, zone.planBlob!);

    if (result.success) {
      await offlineDb.zones.update(zone.id, {
        planUrl: result.planUrl,
        planType: "image",
        planSyncStatus: "synced",
        syncStatus: "pending" as SyncStatus, // Force push pour que le serveur reçoive le planUrl + planType
      });
      count++;
    } else {
      errors.push(`Plan zone ${zone.name}: ${result.error}`);
    }
  }

  return { count, errors };
}

/** Télécharge le plan d'une zone depuis le serveur */
async function pullPlanBlob(planUrl: string): Promise<ArrayBuffer | null> {
  try {
    const token = localStorage.getItem("ultra-mesure-token");
    const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
    const response = await fetch(planUrl, { headers });
    if (!response.ok) {
      console.warn(`[sync] Téléchargement du plan échoué (${response.status}) : ${planUrl}`);
      return null;
    }
    return response.arrayBuffer();
  } catch (err) {
    console.warn(`[sync] Téléchargement du plan en erreur réseau : ${planUrl}`, err);
    return null;
  }
}

/**
 * Télécharge les photos manquantes depuis le serveur.
 * Pour chaque mesure locale, vérifie si les photoIds ont un OfflinePhoto correspondant.
 * Si non, télécharge le blob et le stocke dans offlineDb.photos.
 */
async function pullPhotos(): Promise<{ count: number; errors: string[] }> {
  let count = 0;
  const errors: string[] = [];

  try {
    // Récupérer toutes les mesures qui ont des photos
    const mesures = await offlineDb.mesures
      .filter((m) => m.photos.length > 0)
      .toArray();

    if (mesures.length === 0) return { count, errors };

    // Récupérer les IDs de photos déjà en local
    const localPhotoIds = new Set(
      (await offlineDb.photos.toCollection().primaryKeys()) as string[]
    );

    // Collecter les photos manquantes
    const missing: Array<{ photoId: string; mesureId: string }> = [];
    for (const mesure of mesures) {
      for (const photoId of mesure.photos) {
        if (!localPhotoIds.has(photoId)) {
          missing.push({ photoId, mesureId: mesure.id });
        }
      }
    }

    if (missing.length === 0) return { count, errors };

    // Télécharger par batch de 3
    for (let i = 0; i < missing.length; i += PHOTO_BATCH_SIZE) {
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        errors.push("Connexion perdue pendant le téléchargement des photos");
        break;
      }

      const batch = missing.slice(i, i + PHOTO_BATCH_SIZE);
      const results = await Promise.allSettled(
        batch.map(async ({ photoId, mesureId }) => {
          const url = `/api/sync/photos/${mesureId}/${photoId}`;
          const token = localStorage.getItem("ultra-mesure-token");
          const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
          const response = await fetch(url, { headers });
          if (!response.ok) {
            return { success: false as const, photoId, error: `HTTP ${response.status}` };
          }

          const blob = await response.blob();
          const ext = blob.type.split("/")[1] || "jpg";

          await offlineDb.photos.put({
            id: photoId,
            mesureId,
            blob,
            fileName: `${photoId}.${ext}`,
            serverUrl: url,
            syncStatus: "synced",
          });

          return { success: true as const, photoId };
        })
      );

      for (const r of results) {
        if (r.status === "fulfilled" && r.value.success) {
          count++;
        } else if (r.status === "fulfilled" && !r.value.success) {
          errors.push(`Photo ${r.value.photoId}: ${r.value.error}`);
        } else if (r.status === "rejected") {
          errors.push(`Photo download: ${r.reason}`);
        }
      }
    }
  } catch (err) {
    errors.push(`Pull photos: ${err instanceof Error ? err.message : "Erreur"}`);
  }

  return { count, errors };
}

// ─── Fonctions internes par table ───────────────────────────

async function syncTableGeneric(
  tableName: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getPending: () => Promise<any[]>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  markSynced: (ids: string[]) => Promise<any>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  markError: (ids: string[]) => Promise<any>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cleanFn: (entity: Record<string, any>) => Record<string, any>
): Promise<{ count: number; errors: string[] }> {
  const pending = await getPending();

  if (pending.length === 0) return { count: 0, errors: [] };

  const cleaned = pending.map(cleanFn);
  const result = await pushEntities(tableName, cleaned);
  const allIds = pending.map((e: { id: string }) => e.id);

  if (result.success) {
    // Tout a réussi
    await markSynced(allIds);
    return { count: pending.length, errors: [] };
  } else if (result.failedIds && result.failedIds.length > 0) {
    // Échec partiel : marquer chaque entité individuellement
    const failedSet = new Set(result.failedIds);
    const succeededIds = allIds.filter((id) => !failedSet.has(id));
    const errorIds = allIds.filter((id) => failedSet.has(id));

    if (succeededIds.length > 0) await markSynced(succeededIds);
    if (errorIds.length > 0) await markError(errorIds);

    return {
      count: succeededIds.length,
      errors: [result.error ?? `Erreur sync ${tableName}`],
    };
  } else {
    // Échec total (erreur réseau, HTTP non-200, etc.)
    await markError(allIds);
    return { count: 0, errors: [result.error ?? `Erreur sync ${tableName}`] };
  }
}

/** Champs minimaux requis par makeTableHelpers (communs à toutes les tables sync). */
type SyncableRow = { id: string; syncStatus: SyncStatus; retryCount?: number };

function makeTableHelpers<T extends SyncableRow>(table: EntityTable<T, "id">) {
  return {
    // Ne pousser que les entités pending/error.
    // Les entités "synced" sont déjà sur le serveur : les re-pousser
    // annulerait les suppressions faites depuis un autre appareil.
    // C5 : on cappe les retries des entités en "error" (retryCount >= MAX) pour
    // éviter la boucle d'erreur silencieuse infinie (ex. FK violation). Les
    // entités "pending" (édition utilisateur fraîche) sont toujours retentées.
    getPending: async () => {
      const rows = await table
        .where("syncStatus")
        .anyOf("pending" as SyncStatus, "error" as SyncStatus)
        .toArray();
      return rows.filter(
        (r) => r.syncStatus === "pending" || (r.retryCount ?? 0) < MAX_PUSH_RETRIES
      );
    },
    // Succès : on remet retryCount à 0 (un futur échec repart d'un compteur propre).
    markSynced: (ids: string[]) =>
      table.where("id").anyOf(ids).modify((e) => {
        e.syncStatus = "synced" as SyncStatus;
        e.retryCount = 0;
      }),
    // Échec : on incrémente retryCount pour cap progressif.
    markError: (ids: string[]) =>
      table.where("id").anyOf(ids).modify((e) => {
        e.syncStatus = "error";
        e.retryCount = (e.retryCount ?? 0) + 1;
      }),
  };
}

const PHOTO_BATCH_SIZE = 3;

/**
 * Upload les photos pending par batch de 3 en parallèle.
 * Continue même si certaines échouent (les erreurs sont collectées).
 */
async function syncPhotos(): Promise<{ count: number; errors: string[] }> {
  const pending = await offlineDb.photos
    .where("syncStatus")
    .anyOf("pending" as SyncStatus, "error" as SyncStatus)
    .toArray();

  if (pending.length === 0) return { count: 0, errors: [] };

  let count = 0;
  const errors: string[] = [];

  // Upload par batch de PHOTO_BATCH_SIZE en parallèle
  for (let i = 0; i < pending.length; i += PHOTO_BATCH_SIZE) {
    // Vérifier qu'on est toujours online avant chaque batch
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      errors.push("Connexion perdue pendant l'upload des photos");
      break;
    }

    const batch = pending.slice(i, i + PHOTO_BATCH_SIZE);
    const results = await Promise.allSettled(
      batch.map(async (photo) => {
        const result = await pushPhoto(
          photo.id,
          photo.blob,
          photo.fileName,
          photo.mesureId
        );

        if (result.success) {
          await offlineDb.photos.update(photo.id, {
            serverUrl: result.serverUrl,
            syncStatus: "synced" as SyncStatus,
          });
          return { success: true as const, fileName: photo.fileName };
        } else {
          await offlineDb.photos.update(photo.id, {
            syncStatus: "error" as SyncStatus,
          });
          return { success: false as const, fileName: photo.fileName, error: result.error };
        }
      })
    );

    for (const r of results) {
      if (r.status === "fulfilled" && r.value.success) {
        count++;
      } else if (r.status === "fulfilled" && !r.value.success) {
        errors.push(`Photo ${r.value.fileName}: ${r.value.error}`);
      } else if (r.status === "rejected") {
        errors.push(`Photo upload: ${r.reason}`);
      }
    }
  }

  return { count, errors };
}

// ─── Helpers blob ↔ base64 (pour photos sections custom rapport) ──

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [header, data] = dataUrl.split(",");
  const mime = header.match(/:(.*?);/)?.[1] ?? "image/jpeg";
  const binary = atob(data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function cleanRapportForSync(rapport: OfflineRapport): Promise<Record<string, any>> {
  const { syncStatus, customSections, ...rest } = rapport;
  void syncStatus;

  const cleanedSections = await Promise.all(
    (customSections ?? []).map(async (section: CustomSection) => {
      const { photoBlob, ...sectionRest } = section;
      if (!photoBlob) return sectionRest;
      const photoDataUrl = await blobToDataUrl(photoBlob);
      return { ...sectionRest, photoDataUrl };
    })
  );

  return { ...rest, customSections: cleanedSections };
}

async function syncRapports(): Promise<{ count: number; errors: string[] }> {
  const pending = await offlineDb.rapports
    .where("syncStatus")
    .anyOf("pending" as SyncStatus, "error" as SyncStatus)
    .toArray();

  if (pending.length === 0) return { count: 0, errors: [] };

  const cleaned = await Promise.all(pending.map(cleanRapportForSync));
  const result = await pushEntities("rapports", cleaned);
  const allIds = pending.map((r) => r.id);

  if (result.success) {
    await offlineDb.rapports.where("id").anyOf(allIds).modify({ syncStatus: "synced" as SyncStatus });
    return { count: pending.length, errors: [] };
  } else if (result.failedIds && result.failedIds.length > 0) {
    const failedSet = new Set(result.failedIds);
    const succeededIds = allIds.filter((id) => !failedSet.has(id));
    const errorIds = allIds.filter((id) => failedSet.has(id));
    if (succeededIds.length > 0)
      await offlineDb.rapports.where("id").anyOf(succeededIds).modify({ syncStatus: "synced" as SyncStatus });
    if (errorIds.length > 0)
      await offlineDb.rapports.where("id").anyOf(errorIds).modify({ syncStatus: "error" as SyncStatus });
    return { count: succeededIds.length, errors: [result.error ?? "Erreur sync rapports"] };
  } else {
    await offlineDb.rapports.where("id").anyOf(allIds).modify({ syncStatus: "error" as SyncStatus });
    return { count: 0, errors: [result.error ?? "Erreur sync rapports"] };
  }
}

// ─── Pull (serveur → local) ────────────────────────────────

// Types bruts retournés par l'API /api/sync/pull
interface ServerMesure {
  id: string;
  numero: number;
  photos: string[];
  notes?: string | null;
  reseau?: string | null;
  localisation?: string | null;
  reseauType?: string | null;
  reseauComplement?: string | null;
  reseauSens?: string | null;
  diametreExt?: number | null;
  dn?: number | null;
  orientation?: string | null;
  materiau?: string | null;
  fonteAge?: string | null;
  fonteType?: string | null;
  tarification?: string[] | null;
  mesureMax?: number | null;
  mesureMin?: number | null;
  epaisseurOrigine?: number | null;
  epaisseurManuelle?: boolean | null;
  dnManuel?: boolean | null;
  pourcentageCorrosion?: number | null;
  codeCouleur?: string | null;
  pinId: string;
  campagneId: string;
  createdAt: string;
  updatedAt: string;
}

interface ServerPin {
  id: string;
  number: number;
  x: number;
  y: number;
  siteId: string;
  zoneId: string;
  createdAt: string;
  updatedAt: string;
}

interface ServerCampagne {
  id: string;
  date: string;
  status: string;
  notes?: string | null;
  siteId: string;
  createdAt: string;
  updatedAt: string;
}

interface ServerZone {
  id: string;
  name: string;
  planUrl?: string | null;
  planType: string;
  locked?: boolean;
  order?: number;
  siteId: string;
  createdAt: string;
  updatedAt: string;
}

interface ServerSite {
  id: string;
  name: string;
  client?: string | null;
  address?: string | null;
  userId: string;
  createdAt: string;
  updatedAt: string;
}

interface ServerRapport {
  id: string;
  siteId: string;
  numero: string;
  missionText?: unknown;
  methodologieText?: unknown;
  conclusionsText?: unknown;
  customSections?: unknown; // Array JSON avec potentiellement photoDataUrl
  hiddenSections?: string[] | null;
  destinataire?: string | null;
  destinataireAdresse?: string | null;
  signataireNom?: string | null;
  signataireFonction?: string | null;
  titreCouverture?: string | null;
  sousTitreReseau?: string | null;
  batiment?: string | null;
  notaBene?: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Retire les clés avec valeur `undefined` d'un objet.
 * Dexie interprète `undefined` comme "supprimer ce champ" dans update(),
 * ce qui efface des données au lieu de les préserver.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function stripUndefined<T extends Record<string, any>>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined)
  ) as Partial<T>;
}

/**
 * Récupère les données serveur et met à jour le local (last-write-wins par updatedAt).
 * Respecte l'ordre FK : sites → zones → campagnes → pins → mesures.
 * Ne touche pas aux planBlob ni aux photos.
 *
 * Deux modes :
 * - Delta (si lastSyncAt existe) : ne récupère que les entités modifiées + les suppressions
 * - Full (premier sync) : récupère toute la hiérarchie imbriquée
 */
async function pullFromServer(): Promise<{
  pulled: { sites: number; zones: number; campagnes: number; pins: number; mesures: number };
  errors: string[];
  serverTime?: string;
  preserved?: number;
}> {
  const pulled = { sites: 0, zones: 0, campagnes: 0, pins: 0, mesures: 0 };
  const errors: string[] = [];
  let serverTime: string | undefined;
  let preserved = 0;

  try {
    const headers = await getAuthHeaders();
    const lastSyncAt = getLastSyncAt();

    // Construire l'URL avec ou sans ?since
    let url = "/api/sync/pull";
    if (lastSyncAt) {
      url += `?since=${lastSyncAt.toISOString()}`;
    }

    const response = await fetch(url, { method: "GET", headers });

    if (!response.ok) {
      const text = await response.text();
      errors.push(`Pull échoué: ${response.status}: ${text}`);
      return { pulled, errors };
    }

    const data = await response.json();
    if (!data.success) {
      errors.push("Pull: réponse serveur invalide");
      return { pulled, errors, serverTime };
    }

    // Récupérer le timestamp serveur (pour éviter le décalage d'horloge)
    serverTime = data.serverTime;

    // Détecter le mode de réponse
    if (data.mode === "delta") {
      // ─── Delta pull : entités aplaties + suppressions ───
      const result = await pullDelta(data);
      Object.assign(pulled, result.pulled);
      errors.push(...result.errors);
      preserved += result.preserved;
    } else {
      // ─── Full pull : listes plates par entité (premier sync) ───
      if (!Array.isArray(data.sites)) {
        errors.push("Pull: réponse serveur invalide");
        return { pulled, errors };
      }
      const result = await pullFull(data);
      Object.assign(pulled, result.pulled);
      errors.push(...result.errors);
      preserved += result.preserved;
    }
  } catch (err) {
    errors.push(
      `Pull: ${err instanceof Error ? err.message : "Erreur réseau"}`
    );
  }

  return { pulled, errors, serverTime, preserved };
}

// ─── Types pour la réponse delta ───────────────────────────

interface ServerRapportAnnexe {
  id: string;
  rapportId: string;
  fileName: string;
  fileSize: number;
  order?: number | null;
  customSectionId?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface DeltaResponse {
  sites: ServerSite[];
  zones: ServerZone[];
  campagnes: ServerCampagne[];
  pins: ServerPin[];
  mesures: ServerMesure[];
  rapports?: ServerRapport[];
  annexes?: ServerRapportAnnexe[];
  deletions: Array<{ entityType: string; entityId: string }>;
}

/**
 * Traite un delta pull : upsert des entités modifiées + application des suppressions.
 */
async function pullDelta(data: DeltaResponse): Promise<{
  pulled: { sites: number; zones: number; campagnes: number; pins: number; mesures: number };
  errors: string[];
  preserved: number;
}> {
  const pulled = { sites: 0, zones: 0, campagnes: 0, pins: 0, mesures: 0 };
  const errors: string[] = [];
  let preserved = 0;

  // 1. Upsert les entités modifiées (même logique last-write-wins).
  // Chaque upsert est isolé : si une entité throw (quota IndexedDB, blob
  // corrompu, transaction Safari avortée), on continue avec les suivantes
  // au lieu de stopper toute la sync et de perdre les entités restantes.
  for (const serverSite of data.sites ?? []) {
    try {
      if (await upsertLocalSite(serverSite)) pulled.sites++;
    } catch (err) {
      errors.push(`upsert site ${serverSite.id}: ${err instanceof Error ? err.message : "erreur"}`);
      console.error("[sync] upsert site failed", serverSite.id, err);
    }
  }
  for (const serverZone of data.zones ?? []) {
    try {
      if (await upsertLocalZone(serverZone)) pulled.zones++;
    } catch (err) {
      errors.push(`upsert zone ${serverZone.id}: ${err instanceof Error ? err.message : "erreur"}`);
      console.error("[sync] upsert zone failed", serverZone.id, err);
    }
  }
  for (const serverCampagne of data.campagnes ?? []) {
    try {
      if (await upsertLocalCampagne(serverCampagne)) pulled.campagnes++;
    } catch (err) {
      errors.push(`upsert campagne ${serverCampagne.id}: ${err instanceof Error ? err.message : "erreur"}`);
      console.error("[sync] upsert campagne failed", serverCampagne.id, err);
    }
  }
  for (const serverPin of data.pins ?? []) {
    try {
      if (await upsertLocalPin(serverPin)) pulled.pins++;
    } catch (err) {
      errors.push(`upsert pin ${serverPin.id}: ${err instanceof Error ? err.message : "erreur"}`);
      console.error("[sync] upsert pin failed", serverPin.id, err);
    }
  }
  for (const serverMesure of data.mesures ?? []) {
    try {
      if (await upsertLocalMesure(serverMesure)) pulled.mesures++;
    } catch (err) {
      errors.push(`upsert mesure ${serverMesure.id}: ${err instanceof Error ? err.message : "erreur"}`);
      console.error("[sync] upsert mesure failed", serverMesure.id, err);
    }
  }
  for (const serverRapport of data.rapports ?? []) {
    try {
      await upsertLocalRapport(serverRapport);
    } catch (err) {
      errors.push(`upsert rapport ${serverRapport.id}: ${err instanceof Error ? err.message : "erreur"}`);
      console.error("[sync] upsert rapport failed", serverRapport.id, err);
    }
  }
  for (const serverAnnexe of data.annexes ?? []) {
    try {
      await upsertLocalAnnexe(serverAnnexe);
    } catch (err) {
      errors.push(`upsert annexe ${serverAnnexe.id}: ${err instanceof Error ? err.message : "erreur"}`);
      console.error("[sync] upsert annexe failed", serverAnnexe.id, err);
    }
  }

  // 2. Appliquer les suppressions (cascade locale) : C4 : on NE supprime PAS
  // un sous-arbre contenant des données non synchronisées (pending/error).
  const cascadeTypes = ["site", "zone", "campagne", "pin", "mesure"] as const;
  for (const del of data.deletions ?? []) {
    try {
      // Annexes PDF : suppression simple (pas de cascade), en préservant une
      // annexe locale non synchronisée (blob pas encore uploadé).
      // POLITIQUE ASSUMÉE (post-EUTELSAT, comme C4) : une modification locale
      // non synchronisée GAGNE sur une suppression distante : l'annexe pending
      // sera re-poussée et recréera l'entrée serveur. On préfère annuler une
      // suppression que détruire un fichier qu'un appareil détient encore.
      if (del.entityType === "annexe") {
        const localAnnexe = await offlineDb.rapportAnnexes.get(del.entityId);
        if (localAnnexe && localAnnexe.syncStatus !== "synced") {
          preserved++;
          console.warn(`[sync] Suppression serveur ignorée pour annexe ${del.entityId} : non synchronisée localement (préservée).`);
        } else if (localAnnexe) {
          await offlineDb.rapportAnnexes.delete(del.entityId);
        }
        continue;
      }
      if (!(cascadeTypes as readonly string[]).includes(del.entityType)) continue;
      const deleted = await cascadeDeleteIfFullySynced(
        del.entityType as (typeof cascadeTypes)[number],
        del.entityId
      );
      if (deleted) {
        if (del.entityType === "site") pulled.sites++;
        else if (del.entityType === "zone") pulled.zones++;
        else if (del.entityType === "campagne") pulled.campagnes++;
        else if (del.entityType === "pin") pulled.pins++;
        else if (del.entityType === "mesure") pulled.mesures++;
      } else {
        preserved++;
        console.warn(
          `[sync] Suppression serveur ignorée pour ${del.entityType} ${del.entityId} : contient des données locales non synchronisées (préservées).`
        );
      }
    } catch (err) {
      // Un delete Dexie sur une clé absente ne throw PAS (idempotent) : un throw ici
      // est donc une vraie erreur (transaction avortée, quota, DB bloquée), pas un
      // « déjà supprimé ». On la remonte au lieu de l'avaler.
      errors.push(`suppression ${del.entityType} ${del.entityId}: ${err instanceof Error ? err.message : "erreur"}`);
      console.warn(`[sync] Échec suppression locale ${del.entityType} ${del.entityId}`, err);
    }
  }

  return { pulled, errors, preserved };
}

/**
 * Traite un full pull : listes plates par entité + nettoyage par comparaison d'IDs.
 * Depuis le refactor modèle B, le serveur renvoie des listes plates cohérentes
 * avec la hiérarchie Site → (Zone, Campagne, Pin, Mesure).
 */
async function pullFull(data: {
  sites: ServerSite[];
  zones?: ServerZone[];
  campagnes?: ServerCampagne[];
  pins?: ServerPin[];
  mesures?: ServerMesure[];
  rapports?: ServerRapport[];
  annexes?: ServerRapportAnnexe[];
}): Promise<{
  pulled: { sites: number; zones: number; campagnes: number; pins: number; mesures: number };
  errors: string[];
  preserved: number;
}> {
  const pulled = { sites: 0, zones: 0, campagnes: 0, pins: 0, mesures: 0 };
  const errors: string[] = [];
  let preserved = 0;

  // Upsert dans l'ordre FK : sites, zones, campagnes, pins, mesures, rapports.
  // Chaque upsert est isolé (cf. pullDelta) pour ne pas perdre les entités
  // suivantes si l'une throw.
  for (const s of data.sites ?? []) {
    try {
      if (await upsertLocalSite(s)) pulled.sites++;
    } catch (err) {
      errors.push(`upsert site ${s.id}: ${err instanceof Error ? err.message : "erreur"}`);
      console.error("[sync] upsert site failed", s.id, err);
    }
  }
  for (const z of data.zones ?? []) {
    try {
      if (await upsertLocalZone(z)) pulled.zones++;
    } catch (err) {
      errors.push(`upsert zone ${z.id}: ${err instanceof Error ? err.message : "erreur"}`);
      console.error("[sync] upsert zone failed", z.id, err);
    }
  }
  for (const c of data.campagnes ?? []) {
    try {
      if (await upsertLocalCampagne(c)) pulled.campagnes++;
    } catch (err) {
      errors.push(`upsert campagne ${c.id}: ${err instanceof Error ? err.message : "erreur"}`);
      console.error("[sync] upsert campagne failed", c.id, err);
    }
  }
  for (const p of data.pins ?? []) {
    try {
      if (await upsertLocalPin(p)) pulled.pins++;
    } catch (err) {
      errors.push(`upsert pin ${p.id}: ${err instanceof Error ? err.message : "erreur"}`);
      console.error("[sync] upsert pin failed", p.id, err);
    }
  }
  for (const m of data.mesures ?? []) {
    try {
      if (await upsertLocalMesure(m)) pulled.mesures++;
    } catch (err) {
      errors.push(`upsert mesure ${m.id}: ${err instanceof Error ? err.message : "erreur"}`);
      console.error("[sync] upsert mesure failed", m.id, err);
    }
  }
  for (const r of data.rapports ?? []) {
    try {
      await upsertLocalRapport(r);
    } catch (err) {
      errors.push(`upsert rapport ${r.id}: ${err instanceof Error ? err.message : "erreur"}`);
      console.error("[sync] upsert rapport failed", r.id, err);
    }
  }
  for (const a of data.annexes ?? []) {
    try {
      await upsertLocalAnnexe(a);
    } catch (err) {
      errors.push(`upsert annexe ${a.id}: ${err instanceof Error ? err.message : "erreur"}`);
      console.error("[sync] upsert annexe failed", a.id, err);
    }
  }

  // ─── Nettoyage : supprimer les entités locales qui n'existent plus sur le serveur ───
  const serverSiteIds = new Set((data.sites ?? []).map((s) => s.id));
  const serverZoneIds = new Set((data.zones ?? []).map((z) => z.id));
  const serverCampagneIds = new Set((data.campagnes ?? []).map((c) => c.id));
  const serverPinIds = new Set((data.pins ?? []).map((p) => p.id));
  const serverMesureIds = new Set((data.mesures ?? []).map((m) => m.id));

  // C4 : suppression-par-absence neutralisée : on ne détruit un sous-arbre que
  // s'il est entièrement synchronisé (sinon serveur dégradé = data-loss EUTELSAT).
  const localSites = await offlineDb.sites.where("syncStatus").equals("synced").toArray();
  for (const s of localSites) {
    if (!serverSiteIds.has(s.id)) {
      if (await cascadeDeleteIfFullySynced("site", s.id)) pulled.sites++;
      else preserved++;
    }
  }

  const localZones = await offlineDb.zones.where("syncStatus").equals("synced").toArray();
  for (const z of localZones) {
    if (!serverZoneIds.has(z.id)) {
      if (await cascadeDeleteIfFullySynced("zone", z.id)) pulled.zones++;
      else preserved++;
    }
  }

  const localCampagnes = await offlineDb.campagnes.where("syncStatus").equals("synced").toArray();
  for (const c of localCampagnes) {
    if (!serverCampagneIds.has(c.id)) {
      if (await cascadeDeleteIfFullySynced("campagne", c.id)) pulled.campagnes++;
      else preserved++;
    }
  }

  const localPins = await offlineDb.pins.where("syncStatus").equals("synced").toArray();
  for (const p of localPins) {
    if (!serverPinIds.has(p.id)) {
      if (await cascadeDeleteIfFullySynced("pin", p.id)) pulled.pins++;
      else preserved++;
    }
  }

  const localMesures = await offlineDb.mesures.where("syncStatus").equals("synced").toArray();
  for (const m of localMesures) {
    if (!serverMesureIds.has(m.id)) {
      if (await cascadeDeleteIfFullySynced("mesure", m.id)) pulled.mesures++;
      else preserved++;
    }
  }

  // Nettoyage rapports orphelins (supprimés côté serveur)
  const serverRapportIds = new Set((data.rapports ?? []).map((r) => r.id));
  const localRapports = await offlineDb.rapports.where("syncStatus").equals("synced").toArray();
  for (const r of localRapports) {
    if (!serverRapportIds.has(r.id)) {
      await offlineDb.rapports.delete(r.id);
    }
  }

  // Nettoyage annexes orphelines (supprimées côté serveur) : uniquement les
  // synced, une annexe pending locale n'a jamais atteint le serveur.
  const serverAnnexeIds = new Set((data.annexes ?? []).map((a) => a.id));
  const localAnnexes = await offlineDb.rapportAnnexes.where("syncStatus").equals("synced").toArray();
  for (const a of localAnnexes) {
    if (!serverAnnexeIds.has(a.id)) {
      await offlineDb.rapportAnnexes.delete(a.id);
    }
  }

  return { pulled, errors, preserved };
}

// ─── Helpers upsert local (partagés entre delta et full) ───

/** Vérifie si une suppression est en attente pour cette entité (anti-zombie) */
async function hasPendingDeletion(entityId: string): Promise<boolean> {
  const count = await offlineDb.pendingDeletions
    .where("entityId")
    .equals(entityId)
    .count();
  return count > 0;
}

/** Upsert un site. Retourne true si modifié. */
async function upsertLocalSite(serverSite: ServerSite): Promise<boolean> {
  if (await hasPendingDeletion(serverSite.id)) return false;
  const localSite = await offlineDb.sites.get(serverSite.id);
  const serverDate = new Date(serverSite.updatedAt);

  if (!localSite) {
    await offlineDb.sites.add({
      id: serverSite.id,
      name: serverSite.name,
      client: serverSite.client ?? null,
      address: serverSite.address ?? null,
      userId: serverSite.userId,
      createdAt: new Date(serverSite.createdAt),
      updatedAt: serverDate,
      syncStatus: "synced",
    });
    return true;
  } else if (shouldOverwriteFromServer(localSite.syncStatus, localSite.updatedAt, serverDate)) {
    await offlineDb.sites.update(serverSite.id, stripUndefined({
      name: serverSite.name,
      client: serverSite.client ?? null,
      address: serverSite.address ?? null,
      updatedAt: serverDate,
      syncStatus: "synced" as SyncStatus,
    }));
    return true;
  }
  return false;
}

/** Upsert une zone. Retourne true si modifié. */
async function upsertLocalZone(serverZone: ServerZone): Promise<boolean> {
  if (await hasPendingDeletion(serverZone.id)) return false;
  const localZone = await offlineDb.zones.get(serverZone.id);
  const serverDate = new Date(serverZone.updatedAt);

  if (!localZone) {
    let planBlob: ArrayBuffer | undefined;
    if (serverZone.planUrl) {
      const downloaded = await pullPlanBlob(serverZone.planUrl);
      if (downloaded) planBlob = downloaded;
    }

    await offlineDb.zones.add({
      id: serverZone.id,
      name: serverZone.name,
      planUrl: serverZone.planUrl ?? null,
      planType: serverZone.planType,
      locked: serverZone.locked ?? false,
      order: serverZone.order ?? 0,
      planBlob,
      planSyncStatus: planBlob ? "synced" : undefined,
      siteId: serverZone.siteId,
      createdAt: new Date(serverZone.createdAt),
      updatedAt: serverDate,
      syncStatus: "synced",
    });
    return true;
  } else if (shouldOverwriteFromServer(localZone.syncStatus, localZone.updatedAt, serverDate)) {
    let planUpdate: { planBlob?: ArrayBuffer; planSyncStatus?: "synced" } = {};
    if (serverZone.planUrl) {
      const downloaded = await pullPlanBlob(serverZone.planUrl);
      if (downloaded) {
        planUpdate = { planBlob: downloaded, planSyncStatus: "synced" };
      }
    }

    await offlineDb.zones.update(serverZone.id, stripUndefined({
      name: serverZone.name,
      planUrl: serverZone.planUrl ?? null,
      planType: serverZone.planType,
      locked: serverZone.locked ?? false,
      order: serverZone.order ?? 0,
      ...planUpdate,
      updatedAt: serverDate,
      syncStatus: "synced" as SyncStatus,
    }));
    return true;
  } else if (!localZone.planBlob && serverZone.planUrl) {
    // Le serveur a un plan mais pas nous → le télécharger
    const downloaded = await pullPlanBlob(serverZone.planUrl);
    if (downloaded) {
      await offlineDb.zones.update(serverZone.id, {
        planBlob: downloaded,
        planSyncStatus: "synced",
      });
    }
  }

  // Détection plan local non poussé : on a un planBlob mais le serveur a planUrl: null
  // → forcer le re-upload au prochain syncPlanBlobs
  if (localZone && localZone.planBlob && !serverZone.planUrl) {
    await offlineDb.zones.update(serverZone.id, {
      planSyncStatus: "pending",
    });
  }

  return false;
}

/** Upsert une campagne. Retourne true si modifié. */
async function upsertLocalCampagne(serverCampagne: ServerCampagne): Promise<boolean> {
  if (await hasPendingDeletion(serverCampagne.id)) return false;
  const local = await offlineDb.campagnes.get(serverCampagne.id);
  const serverDate = new Date(serverCampagne.updatedAt);

  if (!local) {
    await offlineDb.campagnes.add({
      id: serverCampagne.id,
      date: new Date(serverCampagne.date),
      status: serverCampagne.status,
      notes: serverCampagne.notes ?? null,
      siteId: serverCampagne.siteId,
      createdAt: new Date(serverCampagne.createdAt),
      updatedAt: serverDate,
      syncStatus: "synced",
    });
    return true;
  } else if (shouldOverwriteFromServer(local.syncStatus, local.updatedAt, serverDate)) {
    await offlineDb.campagnes.update(serverCampagne.id, stripUndefined({
      date: new Date(serverCampagne.date),
      status: serverCampagne.status,
      notes: serverCampagne.notes ?? null,
      updatedAt: serverDate,
      syncStatus: "synced" as SyncStatus,
    }));
    return true;
  }
  return false;
}

/** Upsert un pin. Retourne true si modifié. */
async function upsertLocalPin(serverPin: ServerPin): Promise<boolean> {
  if (await hasPendingDeletion(serverPin.id)) return false;
  const local = await offlineDb.pins.get(serverPin.id);
  const serverDate = new Date(serverPin.updatedAt);

  if (!local) {
    await offlineDb.pins.add({
      id: serverPin.id,
      number: serverPin.number,
      x: serverPin.x,
      y: serverPin.y,
      siteId: serverPin.siteId,
      zoneId: serverPin.zoneId,
      createdAt: new Date(serverPin.createdAt),
      updatedAt: serverDate,
      syncStatus: "synced",
    });
    return true;
  } else if (shouldOverwriteFromServer(local.syncStatus, local.updatedAt, serverDate)) {
    await offlineDb.pins.update(serverPin.id, stripUndefined({
      number: serverPin.number,
      x: serverPin.x,
      y: serverPin.y,
      zoneId: serverPin.zoneId,
      updatedAt: serverDate,
      syncStatus: "synced" as SyncStatus,
    }));
    return true;
  }
  return false;
}

/** Upsert une mesure. Retourne true si modifié. */
async function upsertLocalMesure(serverMesure: ServerMesure): Promise<boolean> {
  if (await hasPendingDeletion(serverMesure.id)) return false;
  const local = await offlineDb.mesures.get(serverMesure.id);
  const serverDate = new Date(serverMesure.updatedAt);

  if (!local) {
    await offlineDb.mesures.add({
      id: serverMesure.id,
      numero: serverMesure.numero,
      photos: serverMesure.photos ?? [],
      notes: serverMesure.notes ?? null,
      reseau: serverMesure.reseau ?? null,
      localisation: serverMesure.localisation ?? null,
      reseauType: serverMesure.reseauType ?? null,
      reseauComplement: serverMesure.reseauComplement ?? null,
      reseauSens: serverMesure.reseauSens ?? null,
      diametreExt: serverMesure.diametreExt ?? null,
      dn: serverMesure.dn ?? null,
      orientation: serverMesure.orientation ?? null,
      materiau: serverMesure.materiau ?? null,
      fonteAge: serverMesure.fonteAge ?? null,
      fonteType: serverMesure.fonteType ?? null,
      tarification: serverMesure.tarification ?? null,
      mesureMax: serverMesure.mesureMax ?? null,
      mesureMin: serverMesure.mesureMin ?? null,
      epaisseurOrigine: serverMesure.epaisseurOrigine ?? null,
      epaisseurManuelle: serverMesure.epaisseurManuelle ?? false,
      dnManuel: serverMesure.dnManuel ?? false,
      pourcentageCorrosion: serverMesure.pourcentageCorrosion ?? null,
      codeCouleur: serverMesure.codeCouleur ?? null,
      pinId: serverMesure.pinId,
      campagneId: serverMesure.campagneId,
      createdAt: new Date(serverMesure.createdAt),
      updatedAt: serverDate,
      syncStatus: "synced",
    });
    return true;
  } else if (shouldOverwriteFromServer(local.syncStatus, local.updatedAt, serverDate)) {
    await offlineDb.mesures.update(serverMesure.id, stripUndefined({
      numero: serverMesure.numero,
      photos: serverMesure.photos ?? [],
      notes: serverMesure.notes ?? null,
      reseau: serverMesure.reseau ?? null,
      localisation: serverMesure.localisation ?? null,
      reseauType: serverMesure.reseauType ?? null,
      reseauComplement: serverMesure.reseauComplement ?? null,
      reseauSens: serverMesure.reseauSens ?? null,
      diametreExt: serverMesure.diametreExt ?? null,
      dn: serverMesure.dn ?? null,
      orientation: serverMesure.orientation ?? null,
      materiau: serverMesure.materiau ?? null,
      fonteAge: serverMesure.fonteAge ?? null,
      fonteType: serverMesure.fonteType ?? null,
      tarification: serverMesure.tarification ?? null,
      mesureMax: serverMesure.mesureMax ?? null,
      mesureMin: serverMesure.mesureMin ?? null,
      epaisseurOrigine: serverMesure.epaisseurOrigine ?? null,
      epaisseurManuelle: serverMesure.epaisseurManuelle ?? false,
      dnManuel: serverMesure.dnManuel ?? false,
      pourcentageCorrosion: serverMesure.pourcentageCorrosion ?? null,
      codeCouleur: serverMesure.codeCouleur ?? null,
      updatedAt: serverDate,
      syncStatus: "synced" as SyncStatus,
    }));
    return true;
  }
  return false;
}

/** Upsert une annexe PDF (métadonnées seulement, le binaire reste sur le
 *  serveur et se télécharge à la demande via serverUrl : carte éditeur et
 *  génération PDF savent déjà le faire). Ne touche jamais au blob local.
 *  Une annexe locale non synchronisée (blob en attente d'upload, renommage
 *  pending) n'est PAS écrasée. Exporté pour les tests. */
export async function upsertLocalAnnexe(server: ServerRapportAnnexe): Promise<boolean> {
  const local = await offlineDb.rapportAnnexes.get(server.id);
  const serverUrl = `/api/sync/annexes/${server.rapportId}/${server.id}.pdf`;

  if (!local) {
    await offlineDb.rapportAnnexes.add({
      id: server.id,
      rapportId: server.rapportId,
      fileName: server.fileName,
      fileSize: server.fileSize,
      order: server.order ?? 0,
      customSectionId: server.customSectionId ?? null,
      serverUrl,
      syncStatus: "synced",
      createdAt: new Date(server.createdAt),
      updatedAt: new Date(server.updatedAt),
    });
    return true;
  }

  if (local.syncStatus !== "synced") return false;

  await offlineDb.rapportAnnexes.update(server.id, {
    fileName: server.fileName,
    fileSize: server.fileSize,
    order: server.order ?? 0,
    customSectionId: server.customSectionId ?? null,
    serverUrl,
    updatedAt: new Date(server.updatedAt),
  });
  return true;
}

/** Upsert un rapport. Retourne true si modifié. */
async function upsertLocalRapport(serverRapport: ServerRapport): Promise<boolean> {
  const local = await offlineDb.rapports.get(serverRapport.id);
  const serverDate = new Date(serverRapport.updatedAt);

  // Reconvertir les photoDataUrl en Blob dans les customSections
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const customSections = Array.isArray(serverRapport.customSections)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ? (serverRapport.customSections as any[]).map((section) => {
        const { photoDataUrl, ...rest } = section;
        if (!photoDataUrl) return rest as CustomSection;
        return { ...rest, photoBlob: dataUrlToBlob(photoDataUrl) } as CustomSection;
      })
    : undefined;

  if (!local) {
    await offlineDb.rapports.add({
      id: serverRapport.id,
      siteId: serverRapport.siteId,
      numero: serverRapport.numero,
      missionText: serverRapport.missionText,
      methodologieText: serverRapport.methodologieText,
      conclusionsText: serverRapport.conclusionsText,
      customSections,
      hiddenSections: Array.isArray(serverRapport.hiddenSections) ? serverRapport.hiddenSections : undefined,
      destinataire: serverRapport.destinataire ?? null,
      destinataireAdresse: serverRapport.destinataireAdresse ?? null,
      signataireNom: serverRapport.signataireNom ?? null,
      signataireFonction: serverRapport.signataireFonction ?? null,
      titreCouverture: serverRapport.titreCouverture ?? null,
      sousTitreReseau: serverRapport.sousTitreReseau ?? null,
      batiment: serverRapport.batiment ?? null,
      notaBene: serverRapport.notaBene ?? null,
      createdAt: new Date(serverRapport.createdAt),
      updatedAt: serverDate,
      syncStatus: "synced",
    });
    return true;
  } else if (shouldOverwriteFromServer(local.syncStatus, local.updatedAt, serverDate)) {
    await offlineDb.rapports.update(serverRapport.id, stripUndefined({
      numero: serverRapport.numero,
      missionText: serverRapport.missionText,
      methodologieText: serverRapport.methodologieText,
      conclusionsText: serverRapport.conclusionsText,
      customSections,
      hiddenSections: Array.isArray(serverRapport.hiddenSections) ? serverRapport.hiddenSections : undefined,
      destinataire: serverRapport.destinataire ?? null,
      destinataireAdresse: serverRapport.destinataireAdresse ?? null,
      signataireNom: serverRapport.signataireNom ?? null,
      signataireFonction: serverRapport.signataireFonction ?? null,
      titreCouverture: serverRapport.titreCouverture ?? null,
      sousTitreReseau: serverRapport.sousTitreReseau ?? null,
      batiment: serverRapport.batiment ?? null,
      notaBene: serverRapport.notaBene ?? null,
      updatedAt: serverDate,
      syncStatus: "synced" as SyncStatus,
    }));
    return true;
  }
  return false;
}

// ─── Fonction principale ────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const identity = (e: Record<string, any>) => e;

// Nettoyage zone : retirer le planBlob et planSyncStatus (pas besoin côté serveur push)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function cleanZone(zone: Record<string, any>) {
  const { planBlob, planSyncStatus, ...rest } = zone;
  void planBlob;
  void planSyncStatus;
  return rest;
}

/**
 * Synchronise les données textuelles (sites → zones → campagnes → pins → mesures).
 * Les photos sont exclues : elles sont gérées séparément via syncPhotosBackground().
 */
async function syncData(): Promise<Omit<SyncResult, "pushed" | "pulled"> & { pushed: Omit<SyncResult["pushed"], "photos"> }> {
  const result = {
    success: true,
    pushed: { sites: 0, zones: 0, campagnes: 0, pins: 0, mesures: 0 },
    errors: [] as string[],
  };

  // 1. Sites
  const sitesH = makeTableHelpers(offlineDb.sites);
  const sites = await syncTableGeneric("sites", sitesH.getPending, sitesH.markSynced, sitesH.markError, identity);
  result.pushed.sites = sites.count;
  result.errors.push(...sites.errors);

  // 2. Zones (sans planBlob)
  const zonesH = makeTableHelpers(offlineDb.zones);
  const zones = await syncTableGeneric("zones", zonesH.getPending, zonesH.markSynced, zonesH.markError, cleanZone);
  result.pushed.zones = zones.count;
  result.errors.push(...zones.errors);

  // 3. Campagnes
  const campagnesH = makeTableHelpers(offlineDb.campagnes);
  const campagnes = await syncTableGeneric("campagnes", campagnesH.getPending, campagnesH.markSynced, campagnesH.markError, identity);
  result.pushed.campagnes = campagnes.count;
  result.errors.push(...campagnes.errors);

  // 4. Pins
  const pinsH = makeTableHelpers(offlineDb.pins);
  const pins = await syncTableGeneric("pins", pinsH.getPending, pinsH.markSynced, pinsH.markError, identity);
  result.pushed.pins = pins.count;
  result.errors.push(...pins.errors);

  // 5. Mesures
  const mesuresH = makeTableHelpers(offlineDb.mesures);
  const mesures = await syncTableGeneric("mesures", mesuresH.getPending, mesuresH.markSynced, mesuresH.markError, identity);
  result.pushed.mesures = mesures.count;
  result.errors.push(...mesures.errors);

  // 6. Rapports (async clean, géré à part)
  const rapports = await syncRapports();
  result.errors.push(...rapports.errors);

  // 7. Annexes PDF : upload des blobs en attente + push des métadonnées
  // (renommage/réordonnancement). Après les rapports : FK annexe -> rapport.
  const annexes = await syncAnnexes();
  result.errors.push(...annexes.errors);

  if (result.errors.length > 0) {
    result.success = false;
  }

  return result;
}

/**
 * Synchronise toutes les données locales "pending" vers le serveur.
 * 1. D'abord les données textuelles (rapide, léger)
 * 2. Puis les photos en arrière-plan (par batch de 3)
 * Les photos ne bloquent pas le retour de la sync des données.
 */
export async function syncAll(): Promise<SyncResult> {
  const release = await acquireSyncLock();
  try {
    return await _syncAllInner();
  } finally {
    release();
  }
}

// ─── Push des suppressions offline ────────────────────────────

function getDeleteEndpoint(entityType: string, entityId: string): string {
  switch (entityType) {
    case "site": return `/api/sites/${entityId}`;
    case "zone": return `/api/zones/${entityId}`;
    case "campagne": return `/api/campagnes/${entityId}`;
    case "pin": return `/api/pins/${entityId}`;
    case "mesure": return `/api/mesures/${entityId}`;
    case "photo": return `/api/photos/${entityId}`;
    case "annexe": return `/api/sync/annexes/${entityId}`;
    default: throw new Error(`Type inconnu: ${entityType}`);
  }
}

/**
 * Pousse les annexes PDF pending/error vers le serveur.
 * - Avec blob local : upload complet (FormData, comme addAnnexe).
 * - Sans blob (renommage/réordonnancement d'une annexe déjà uploadée) :
 *   POST métadonnées seules, le serveur accepte file absent si l'annexe existe.
 */
async function syncAnnexes(): Promise<{ count: number; errors: string[] }> {
  const rows = await offlineDb.rapportAnnexes
    .where("syncStatus")
    .anyOf(["pending", "error"])
    .toArray();
  // C5 : cap des retries sur les erreurs répétées (mêmes règles que les
  // autres tables) : une pending (édition fraîche) est toujours retentée.
  const pending = rows.filter(
    (a) => a.syncStatus === "pending" || (a.retryCount ?? 0) < MAX_PUSH_RETRIES
  );
  if (pending.length === 0) return { count: 0, errors: [] };

  let count = 0;
  const errors: string[] = [];
  const token = localStorage.getItem("ultra-mesure-token");
  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

  for (const annexe of pending) {
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      errors.push("Connexion perdue pendant le push des annexes");
      break;
    }
    try {
      const formData = new FormData();
      if (annexe.blob && annexe.blob.size > 0) {
        formData.append("file", annexe.blob, annexe.fileName);
      }
      formData.append("annexeId", annexe.id);
      formData.append("rapportId", annexe.rapportId);
      formData.append("fileName", annexe.fileName);
      formData.append("order", String(annexe.order ?? 0));
      if (annexe.customSectionId) formData.append("customSectionId", annexe.customSectionId);

      const response = await fetch("/api/sync/annexes", { method: "POST", headers, body: formData });
      if (response.ok) {
        const data = await response.json().catch(() => ({} as { url?: string }));
        await offlineDb.rapportAnnexes.update(annexe.id, {
          serverUrl: data.url ?? `/api/sync/annexes/${annexe.rapportId}/${annexe.id}.pdf`,
          syncStatus: "synced" as SyncStatus,
          retryCount: 0,
        });
        count++;
      } else if (response.status === 404) {
        // 404 = le rapport parent n'existe plus côté serveur (supprimé).
        // L'annexe est orpheline et ne pourra JAMAIS être uploadée : on la
        // purge localement au lieu de la laisser en erreur à vie (cas vécu :
        // annexe zombie de juin 2026 dont l'upload avait échoué en silence,
        // rapport supprimé depuis, indicateur sync rouge permanent).
        console.warn(
          `[sync] Annexe orpheline purgée (rapport ${annexe.rapportId} inexistant) : ${annexe.fileName}`
        );
        await offlineDb.rapportAnnexes.delete(annexe.id);
        count++;
      } else {
        await offlineDb.rapportAnnexes.update(annexe.id, {
          syncStatus: "error" as SyncStatus,
          retryCount: (annexe.retryCount ?? 0) + 1,
        });
        const text = await response.text().catch(() => "");
        errors.push(`Annexe ${annexe.fileName}: ${response.status} ${text}`);
      }
    } catch (err) {
      await offlineDb.rapportAnnexes.update(annexe.id, {
        syncStatus: "error" as SyncStatus,
        retryCount: (annexe.retryCount ?? 0) + 1,
      });
      errors.push(`Annexe ${annexe.fileName}: ${err instanceof Error ? err.message : "Erreur"}`);
    }
  }

  return { count, errors };
}

async function pushDeletions(): Promise<{ count: number; errors: string[] }> {
  const pending = await offlineDb.pendingDeletions
    .where("syncStatus")
    .anyOf(["pending", "error"])
    .toArray();

  if (pending.length === 0) return { count: 0, errors: [] };

  let count = 0;
  const errors: string[] = [];
  const headers = await getAuthHeaders();

  for (const deletion of pending) {
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      errors.push("Connexion perdue pendant le push des suppressions");
      break;
    }

    try {
      const endpoint = getDeleteEndpoint(deletion.entityType, deletion.entityId);
      const response = await fetch(endpoint, { method: "DELETE", headers });

      if (response.ok || response.status === 404) {
        // 404 = déjà supprimé côté serveur, c'est OK
        await offlineDb.pendingDeletions.delete(deletion.id!);
        count++;
      } else {
        await offlineDb.pendingDeletions.update(deletion.id!, {
          syncStatus: "error" as SyncStatus,
        });
        const text = await response.text();
        errors.push(`Delete ${deletion.entityType}/${deletion.entityId}: ${response.status} ${text}`);
      }
    } catch (err) {
      await offlineDb.pendingDeletions.update(deletion.id!, {
        syncStatus: "error" as SyncStatus,
      });
      errors.push(
        `Delete ${deletion.entityType}/${deletion.entityId}: ${err instanceof Error ? err.message : "Erreur"}`
      );
    }
  }

  return { count, errors };
}

async function _syncAllInner(): Promise<SyncResult> {
  const result: SyncResult = {
    success: true,
    pushed: { sites: 0, zones: 0, campagnes: 0, pins: 0, mesures: 0, photos: 0 },
    pulled: { sites: 0, zones: 0, campagnes: 0, pins: 0, mesures: 0, photos: 0 },
    errors: [],
  };

  // Guard : ne rien tenter si offline (certains appareils rapportent onLine=true en mode avion,
  // mais le SW interceptera les requêtes avec une 503 → on court-circuite ici aussi)
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    result.success = false;
    result.errors.push("Appareil hors ligne, sync reportée");
    return result;
  }

  // Garde-fou bootstrap : si Dexie a été vidé (éviction OS Safari iOS,
  // mode privé, clear cache du navigateur) mais lastSyncAt est toujours posé
  // en localStorage, le prochain delta pull renverrait 0 entité et l'app
  // resterait vide à jamais. On reset lastSyncAt pour forcer un full pull.
  const lastSyncAt = getLastSyncAt();
  if (lastSyncAt) {
    const sitesCount = await offlineDb.sites.count();
    if (sitesCount === 0) {
      console.warn(
        "[sync] Bootstrap detection: Dexie vide alors que lastSyncAt est posé. Reset lastSyncAt pour forcer un full pull."
      );
      if (typeof localStorage !== "undefined") {
        localStorage.removeItem("ultra-mesure-last-sync-v2");
      }
    }
  }

  // Phase 0 : push les suppressions offline vers le serveur (AVANT le pull)
  // Essentiel : empêche le pull de recréer les entités supprimées offline.
  const deletions = await pushDeletions();
  result.errors.push(...deletions.errors);

  // Phase 1 : pull données serveur → local (AVANT le push)
  // Essentiel : permet de supprimer localement les entités supprimées
  // depuis un autre appareil AVANT de re-pousser quoi que ce soit.
  const pull = await pullFromServer();
  result.pulled.sites = pull.pulled.sites;
  result.pulled.zones = pull.pulled.zones;
  result.pulled.campagnes = pull.pulled.campagnes;
  result.pulled.pins = pull.pulled.pins;
  result.pulled.mesures = pull.pulled.mesures;
  result.serverTime = pull.serverTime;
  result.preservedUnsynced = pull.preserved ?? 0;
  result.errors.push(...pull.errors);

  // Phase 1b : télécharger les photos manquantes (après le pull des mesures)
  const pulledPhotos = await pullPhotos();
  result.pulled.photos = pulledPhotos.count;
  result.errors.push(...pulledPhotos.errors);

  // Phase 2 : upload plans (AVANT le push texte, pour que planUrl soit à jour)
  const plans = await syncPlanBlobs();
  result.errors.push(...plans.errors);

  // Phase 3 : push données textuelles (inclut le planUrl mis à jour)
  const data = await syncData();
  result.pushed.sites = data.pushed.sites;
  result.pushed.zones = data.pushed.zones;
  result.pushed.campagnes = data.pushed.campagnes;
  result.pushed.pins = data.pushed.pins;
  result.pushed.mesures = data.pushed.mesures;
  result.errors.push(...data.errors);

  // Phase 4 : upload photos (par batch, plus lent)
  const photos = await syncPhotos();
  result.pushed.photos = photos.count;
  result.errors.push(...photos.errors);

  // C5 : nombre d'entités bloquées (échec répété, retries cappés) → alerte UI
  result.stuckCount = await countStuckEntities();

  if (result.errors.length > 0) {
    result.success = false;
  }

  return result;
}

/**
 * Synchronise uniquement les photos en attente (background).
 * Utile pour lancer un upload photos sans re-syncer les données.
 */
export async function syncPhotosBackground(): Promise<{ count: number; errors: string[] }> {
  return syncPhotos();
}

/**
 * Compte le nombre total d'entités en attente de sync.
 */
export async function getPendingCount(): Promise<number> {
  const unsyncedStatuses = ["pending", "error"];
  const [sites, zones, campagnes, pins, mesures, photos, rapports, annexesCount, pendingDels] = await Promise.all([
    offlineDb.sites.where("syncStatus").anyOf(unsyncedStatuses).count(),
    offlineDb.zones.where("syncStatus").anyOf(unsyncedStatuses).count(),
    offlineDb.campagnes.where("syncStatus").anyOf(unsyncedStatuses).count(),
    offlineDb.pins.where("syncStatus").anyOf(unsyncedStatuses).count(),
    offlineDb.mesures.where("syncStatus").anyOf(unsyncedStatuses).count(),
    offlineDb.photos.where("syncStatus").anyOf(unsyncedStatuses).count(),
    offlineDb.rapports.where("syncStatus").anyOf(unsyncedStatuses).count(),
    offlineDb.rapportAnnexes.where("syncStatus").anyOf(unsyncedStatuses).count(),
    offlineDb.pendingDeletions.where("syncStatus").anyOf(unsyncedStatuses).count(),
  ]);

  return sites + zones + campagnes + pins + mesures + photos + rapports + annexesCount + pendingDels;
}

/**
 * Nombre d'entités bloquées (en `error` au-delà du cap de retries C5) : elles ne
 * sont plus re-poussées automatiquement et doivent être remontées à l'utilisateur.
 */
export async function getStuckCount(): Promise<number> {
  return countStuckEntities();
}

/**
 * Récupère la date de dernière sync réussie (stockée en localStorage).
 */
export function getLastSyncAt(): Date | null {
  if (typeof localStorage === "undefined") return null;
  const stored = localStorage.getItem("ultra-mesure-last-sync-v2");
  return stored ? new Date(stored) : null;
}

/**
 * Met à jour la date de dernière sync réussie.
 */
export function setLastSyncAt(date: Date) {
  if (typeof localStorage !== "undefined") {
    localStorage.setItem("ultra-mesure-last-sync-v2", date.toISOString());
  }
}

/**
 * Migrations client exécutées au démarrage (une seule fois par user).
 *
 * Chaque migration pose un flag dans localStorage après exécution pour ne pas se rejouer.
 * Pour forcer une nouvelle resync complète chez tous les clients, ajouter une nouvelle
 * entrée avec un nouveau nom de flag (jamais modifier les anciennes).
 *
 * Historique :
 * - modeleB-fullresync-v1 (session 38, 2026-04-24) : après le refactor modèle B et les
 *   consolidations SQL prod, certains clients ont un Dexie désynchronisé (ex: 6 mesures
 *   locales pour Le Montaigne au lieu de 33). Reset du lastSyncTime → full pull.
 * - campagnes-zombies-fullresync-v2 (session 39, 2026-04-24 soir) : nettoyage SQL des
 *   9 campagnes zombies Montaigne + 2 mesures vides côté serveur. On re-force un full
 *   pull pour que les clients récupèrent l'état propre sans dépendre du delta pull.
 * - dexie-desync-fullresync-v3 (session 51, 2026-06-03) : Renaud avait des mesures
 *   Nina Ricci sur le serveur invisibles dans son Dexie (probable éviction IDB Safari
 *   iOS ou exception silencieuse pendant un pull antérieur). Reset lastSyncAt pour
 *   forcer un full pull et rapatrier tout ce que le serveur a, chez tous les clients.
 */
export function runClientMigrations() {
  if (typeof localStorage === "undefined") return;

  const FLAGS = [
    "ultra-mesure-migration-modeleB-fullresync-v1",
    "ultra-mesure-migration-campagnes-zombies-fullresync-v2",
    "ultra-mesure-migration-dexie-desync-fullresync-v3",
  ];

  for (const flag of FLAGS) {
    if (!localStorage.getItem(flag)) {
      localStorage.removeItem("ultra-mesure-last-sync-v2");
      localStorage.setItem(flag, new Date().toISOString());
    }
  }
}
